/**
 * Templates Service
 * Operations for SendGrid email templates and merge field management
 */

import { supabase } from "./supabase";

// ========================================
// TYPES
// ========================================

export interface SendGridTemplateVersion {
  id: string;
  name: string;
  subject: string;
  updated_at: string;
}

export interface SendGridTemplate {
  id: string;
  name: string;
  generation: string;
  updated_at: string;
  active_version: SendGridTemplateVersion | null;
}

export interface SendGridTemplateDetails {
  id: string;
  name: string;
  generation: string;
  updated_at: string;
  version: {
    id: string;
    name: string;
    subject: string;
    updated_at: string;
  };
  preview: string;
  html_content?: string;
  merge_fields: string[];
}

/**
 * Operators allowed in a lookup mapping's filter expression.
 *   eq / ne          — exact match (case-insensitive trim compare)
 *   in / not_in      — membership in a list (also case-insensitive)
 *   is_truthy        — yes / true / 1 / y (case-insensitive)
 *   is_falsy         — no  / false / 0 / n / "" (case-insensitive)
 *
 * Spec lives at docs/AUDIT_PROMPT_LOOKUPS_PHASE_1_ROUND3.md (filter
 * semantics). Edge-function side mirrors this list verbatim.
 */
export type LookupFilterOperator =
  | "eq"
  | "ne"
  | "in"
  | "not_in"
  | "is_truthy"
  | "is_falsy";

export interface LookupFilterRule {
  column: string;
  operator: LookupFilterOperator;
  /** string or string[] — array required for in / not_in; ignored for is_truthy / is_falsy */
  value?: string | string[];
}

export interface FieldMapping {
  templateField: string;
  sourceType:
    | "contact"
    | "voucher"
    | "content"
    | "custom"
    | "url"
    | "sendgrid"
    // 'landing_page' tells the flight UI to render the URL-builder panel
    // for this variable. The actual URL is built at send time on the server
    // from `metadata.landing_page_config` — the flight form keeps that
    // config in sync with the mapping. Backward compat: old flights stored
    // landing-page state only in `metadata.landing_page_config` (no mapping
    // entry); the form synthesises a `landing_page` mapping on load.
    | "landing_page"
    // 'lookup' resolves to a value from a campaign_lookups row at send
    // time. lookupId / lookupValueColumn / lookupKeyContactField are
    // required; lookupFilter and lookupFallback are optional. See
    // docs/AUDIT_PROMPT_LOOKUPS_PHASE_1_ROUND3.md for resolution
    // semantics (match → filter → default → fallback → empty).
    | "lookup";
  sourceField?: string;
  customValue?: string;

  // Populated only when sourceType === 'lookup'
  lookupId?: string;
  lookupValueColumn?: string;
  lookupKeyContactField?: string;
  /**
   * `keyed` (default/backward-compatible) matches a contact field to a row.
   * `default_row` reads the lookup's explicitly-marked default row for every
   * recipient. The latter is intended for newsletter/content bundles where
   * one row supplies several template fields and no recipient key is needed.
   */
  lookupResolution?: "keyed" | "default_row";
  lookupFilter?: LookupFilterRule[];
  lookupFallback?: string;
}

/**
 * In-memory lookup table indexed for O(1) per-contact resolution.
 * Built once per send (or coverage call) and passed into every
 * resolveLookupMapping invocation.
 */
export interface LookupBucket {
  /** lower(trim(key_value_normalized)) → row data jsonb */
  byKey: Map<string, Record<string, unknown>>;
  /** the row marked is_default=true, if any */
  defaultRow: Record<string, unknown> | null;
}

/** Outcome of resolving one lookup mapping for one contact. */
export type LookupResolveBucket =
  | "matched"
  | "filter_excluded"
  | "default_used"
  | "fallback_used"
  | "empty_key"
  // Static-content-bundle (default_row) mode with no usable starred default row. Mirrors the edge
  // helper so preview/coverage surface a missing default distinctly from an ordinary fallback (the
  // send path fails closed on this rather than sending blank shared content).
  | "default_row_missing";

export interface LookupResolveOutcome {
  bucket: LookupResolveBucket;
  resolvedValue: string;
}

/**
 * Map every template field whose name matches a lookup value column to the
 * lookup's default row. Matching is case-insensitive/trimmed, but ambiguous
 * duplicate column names are skipped rather than guessed.
 */
export function buildStaticLookupBundleMappings(input: {
  mergeFields: readonly string[];
  mappings: readonly FieldMapping[];
  lookupId: string;
  keyColumn: string;
  columnNames: readonly string[];
}): { mappings: FieldMapping[]; matchedFields: string[] } {
  const columns = new Map<string, string | null>();
  for (const name of input.columnNames) {
    if (name === input.keyColumn) {
      continue;
    }
    const normalized = name.trim().toLowerCase();
    if (!normalized) {
      continue;
    }
    columns.set(normalized, columns.has(normalized) ? null : name);
  }

  const next = [...input.mappings];
  const matchedFields: string[] = [];
  for (const field of input.mergeFields) {
    if (isSendGridReservedField(field)) {
      continue;
    }
    const valueColumn = columns.get(field.trim().toLowerCase());
    if (!valueColumn) {
      continue;
    }
    const existingIndex = next.findIndex((m) => m.templateField === field);
    const replacement: FieldMapping = {
      ...(existingIndex >= 0 ? next[existingIndex] : { templateField: field }),
      templateField: field,
      sourceType: "lookup",
      lookupId: input.lookupId,
      lookupValueColumn: valueColumn,
      lookupResolution: "default_row",
      // A static-content-bundle field resolves from the starred default row only. A leftover
      // recipient-key column, per-row filter, or fallback carried over from a prior KEYED mapping
      // would be applied to the default row (see resolveLookupMapping) and could silently blank it —
      // clear them so the default row is taken as-is.
      lookupKeyContactField: undefined,
      lookupFilter: undefined,
      lookupFallback: undefined,
    };
    if (existingIndex >= 0) {
      next[existingIndex] = replacement;
    } else {
      next.push(replacement);
    }
    matchedFields.push(field);
  }
  return { mappings: next, matchedFields };
}

// ========================================
// CONTACT FIELDS AVAILABLE FOR MAPPING
// ========================================

export const CONTACT_FIELDS = [
  // Identity
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "full_name", label: "Full Name" },
  { value: "email", label: "Email" },
  { value: "phone_number", label: "Phone Number" },
  { value: "title", label: "Title / Job Title" },
  // Location
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "postal_code", label: "Zip / Postal Code" },
  // Membership
  { value: "member_status", label: "Member Status" },
  { value: "member_start_date", label: "Member Since" },
  { value: "member_end_date", label: "Member Expiration" },
  { value: "membership_id", label: "Member Number" },
  { value: "membership_type", label: "Membership Type" },
  // Zuora billing account identifier (GUID). DISTINCT from Member Number
  // (membership_id) — do not conflate. Exposed for explicit operator mapping,
  // e.g. a partner landing link's ZID parameter.
  { value: "zuora_account_id", label: "Zuora Account ID" },
  // CRM
  { value: "lifecycle_stage", label: "Lifecycle Stage" },
] as const;

export const VOUCHER_FIELDS = [
  { value: "voucher_code", label: "Voucher Code" },
  { value: "voucher_amount", label: "Voucher Amount ($)" },
  { value: "voucher_name", label: "Voucher Name" },
  { value: "expiration_date", label: "Expiration Date" },
  { value: "qr_image_url", label: "QR Code Image URL" },
] as const;

export const SYSTEM_FIELDS = [
  { value: "current_date", label: "Current Date" },
  { value: "current_year", label: "Current Year" },
] as const;

/**
 * Template merge fields that the send path always populates without
 * requiring an explicit user-defined mapping. The contact's first_name,
 * last_name, and email are always passed in dynamic_template_data.
 * `subject` and `preheader` are conditionally auto-injected — callers
 * compute the per-flight set themselves and pass it to
 * computeUnmappedTemplateVariables.
 */
export const SYSTEM_FALLBACK_FIELDS = new Set<string>([
  "email",
  "first_name",
  "last_name",
]);

// Forward-declared placeholder; the actual Set is derived from
// SENDGRID_RESERVED_FIELDS lower in this module to keep its membership
// single-sourced. See SENDGRID_AUTO_INJECTED_FIELDS export below
// (defined after the SENDGRID_RESERVED_FIELDS object).

/** Whitespace-only or unset counts as empty. */
function blankInput(s: string | undefined): boolean {
  return !s || !s.trim();
}

/**
 * A mapping that is "present" but resolves to nothing at send time: the operator
 * picked a source type yet left its required input empty, so it renders as an
 * empty string in the email (almost always operator error). Mirrors exactly the
 * per-sourceType inputs the resolver reads — contact/voucher/content -> sourceField,
 * custom/url -> customValue, lookup -> lookupId + valueColumn + keyContactField.
 * landing_page resolves server-side and sendgrid is auto-injected, so neither is
 * treated as empty here.
 */
function isMappingEffectivelyEmpty(mapping: FieldMapping): boolean {
  switch (mapping.sourceType) {
    case "custom":
    case "url":
      return blankInput(mapping.customValue);
    case "contact":
    case "voucher":
    case "content":
      return blankInput(mapping.sourceField);
    case "lookup":
      return (
        blankInput(mapping.lookupId) ||
        blankInput(mapping.lookupValueColumn) ||
        (mapping.lookupResolution !== "default_row" &&
          blankInput(mapping.lookupKeyContactField))
      );
    default:
      return false;
  }
}

/**
 * Return the SendGrid merge fields that are present in the template body but have
 * no resolution at send time — either no FieldMapping targets them, or the
 * mapping's source type is missing its required input (see
 * isMappingEffectivelyEmpty: empty custom/url value, empty contact/voucher/content
 * field, or an under-configured lookup), which renders as an empty string in the
 * email and is almost always operator error.
 *
 * Shared between PreSendConfirmationModal (the schedule-button gate) and FlightForm
 * (the strict-on-save gate). When the two surfaces disagree, operators can save a
 * flight that the scheduler will then refuse to send — surfaced 2026-05-14 as an
 * operator-flagged UX bug.
 */
export function computeUnmappedTemplateVariables(
  mergeFields: readonly string[],
  fieldMappings: readonly FieldMapping[],
  autoInjected: ReadonlySet<string>,
): string[] {
  const byField = new Map<string, FieldMapping>();
  for (const m of fieldMappings) {
    byField.set(m.templateField, m);
  }
  const unmapped: string[] = [];
  for (const variable of mergeFields) {
    if (autoInjected.has(variable.toLowerCase())) {
      continue;
    }
    const mapping = byField.get(variable);
    if (!mapping) {
      unmapped.push(variable);
      continue;
    }
    if (isMappingEffectivelyEmpty(mapping)) {
      unmapped.push(variable);
    }
  }
  return unmapped;
}

/**
 * SendGrid reserved fields that are auto-populated at send time.
 * These should not be manually mapped by the user.
 */
export const SENDGRID_RESERVED_FIELDS: Record<
  string,
  { label: string; description: string }
> = {
  crm_unsubscribe_url: {
    label: "CRM Unsubscribe URL",
    description:
      "Server-generated, signed link to the public U.S. LawShield unsubscribe page. " +
      "Use {{{crm_unsubscribe_url}}} as the link URL in SendGrid. It requires an Email " +
      "subscription category on the flight or sequence and must not be manually mapped.",
  },
  crm_preferences_url: {
    label: "CRM Subscription Preferences URL",
    description:
      "Server-generated, signed link to the public U.S. LawShield preference center. " +
      "Use {{{crm_preferences_url}}} as the link URL in SendGrid. It requires an Email " +
      "subscription category on the flight or sequence and must not be manually mapped.",
  },
  unsubscribe: {
    label: "Unsubscribe URL",
    description:
      "Do NOT map this. For a working per-category unsubscribe, use {{{crm_unsubscribe_url}}} " +
      "as the link URL in the SendGrid template (filled in only when the flight/sequence has an " +
      "Email subscription category). Do NOT use SendGrid's own <%asm_group_unsubscribe_raw_url%> " +
      "tag: it unsubscribes on a plain page load, so email security scanners unsubscribe people " +
      "automatically, and sends with it are refused. A plain {{unsubscribe}} field renders empty.",
  },
  unsubscribe_preferences: {
    label: "Email Preferences URL",
    description:
      "Do NOT map this. For a working preferences page, use {{{crm_preferences_url}}} as the link " +
      "URL in the SendGrid template (filled in only when the flight/sequence has an Email " +
      "subscription category). Do NOT use SendGrid's own <%asm_preferences_raw_url%> tag — " +
      "security scanners trigger it, and sends with it are refused.",
  },
  weblink: {
    label: "View in Browser",
    description: "Link to view email in browser",
  },
  sender_name: {
    label: "Sender Name",
    description: "From name set in SendGrid sender identity",
  },
  sender_address: {
    label: "Sender Address",
    description: "Physical address (CAN-SPAM compliance)",
  },
  sender_city: {
    label: "Sender City",
    description: "City from SendGrid sender identity",
  },
  sender_state: {
    label: "Sender State",
    description: "State from SendGrid sender identity",
  },
  sender_zip: {
    label: "Sender Zip",
    description: "Zip code from SendGrid sender identity",
  },
};

/**
 * Template variables that SendGrid auto-resolves at send time via the ASM
 * (Advanced Suppression Management) group or sender identity. These names
 * appear in templates as `{{unsubscribe}}` / `{{unsubscribe_preferences}}`
 * etc., and operators legitimately leave them unmapped — SendGrid substitutes
 * them per-recipient. Derived from SENDGRID_RESERVED_FIELDS so the set
 * automatically grows when new reserved fields are added there.
 *
 * Note on `unsubscribe` / `unsubscribe_preferences`: these resolve to working
 * links ONLY when the flight/sequence has an Email subscription category
 * selected (the category carries the SendGrid group ID). Operators must not map
 * them as contact variables or embed a numeric group ID in the template body —
 * SendGrid injects the correct per-recipient URL at send time.
 *
 * Both FlightForm (strict-on-save gate) and PreSendConfirmationModal
 * (schedule-button gate) union this into their per-flight autoInjected
 * sets so `computeUnmappedTemplateVariables` doesn't flag them as
 * unmapped.
 *
 * Added 2026-05-28 after an operator hit "Cannot schedule — these template
 * variables aren't mapped: unsubscribe, unsubscribe_preferences" on a
 * newsletter flight whose template body uses the standard SendGrid
 * unsubscribe links.
 */
export const SENDGRID_AUTO_INJECTED_FIELDS = new Set<string>(
  Object.keys(SENDGRID_RESERVED_FIELDS),
);

/**
 * Identity merge-field forms the quick-email server personalizes from the SERVER-loaded
 * contact. Matched case-insensitively AFTER stripping non-alphanumerics, so `First_Name`,
 * `firstName`, `fname`, and `{{name}}` all resolve. Mirrors the allowlist in
 * supabase/functions/send-contact-quick-email/logic.ts (buildContactTemplateData) — kept in
 * sync with it, exactly as SendEmailModal's own PERSONALIZABLE_CANONICAL hint does.
 */
const QUICK_EMAIL_IDENTITY_CANONICAL = new Set<string>([
  "firstname",
  "fname",
  "lastname",
  "lname",
  "fullname",
  "name",
  "email",
  "emailaddress",
]);

/**
 * Contact quick-email (send-contact-quick-email) personalizes ONLY the identity fields above,
 * ALWAYS injects the operator's `subject` (so a `{{{subject}}}` template resolves), and leaves
 * SendGrid to auto-resolve its own ASM/sender tokens (SENDGRID_AUTO_INJECTED_FIELDS:
 * unsubscribe, unsubscribe_preferences, weblink, sender_*); `preheader` is cosmetic and renders
 * harmlessly. Any OTHER merge field a template declares (voucher_code, offer_name, …) has no
 * value source in a quick email and would render as an empty string — so such a template is
 * unsafe to quick-send. Returns the unsupported field names (case preserved); an empty array
 * means the template is safe to use for a contact quick email.
 */
export function quickEmailUnsupportedMergeFields(
  mergeFields: readonly string[] | null | undefined,
): string[] {
  const out: string[] = [];
  for (const f of mergeFields ?? []) {
    const lower = f.toLowerCase();
    const canonical = lower.replace(/[^a-z0-9]/g, "");
    if (QUICK_EMAIL_IDENTITY_CANONICAL.has(canonical)) {
      continue; // first / last / full name, email (any casing / separators)
    }
    if (lower === "subject" || lower === "preheader") {
      continue; // subject is always injected; preheader is cosmetic
    }
    if (SENDGRID_AUTO_INJECTED_FIELDS.has(lower)) {
      continue; // SendGrid ASM / sender tokens
    }
    out.push(f);
  }
  return out;
}

/**
 * Handlebars tokens that are BROKEN for CRM-sent (API) dynamic templates (audit R2-H2). A
 * plain `{{unsubscribe}}` / `{{unsubscribe_preferences}}` renders as an empty link on an API
 * send, silently shipping a mailing with a dead unsubscribe. The working, scanner-safe links
 * are `{{{crm_unsubscribe_url}}}` / `{{{crm_preferences_url}}}`, filled in only when the
 * flight/sequence has an Email subscription category selected. These stay in
 * SENDGRID_AUTO_INJECTED_FIELDS (so they aren't ALSO reported as "unmapped"); the gates below
 * hard-block them with the exact replacement instead.
 */
export const FORBIDDEN_UNSUBSCRIBE_TOKENS: Record<string, string> = {
  // 2026-09-30: the replacements are OUR scanner-safe links, not SendGrid's raw ASM tags. The
  // raw tags unsubscribe on a bare GET, so mail-security scanners unsubscribed people; the old
  // advice here is how 29 templates regressed after the 09-11 migration.
  unsubscribe: "{{{crm_unsubscribe_url}}}",
  unsubscribe_preferences: "{{{crm_preferences_url}}}",
};

/** Which forbidden unsubscribe tokens appear in a template's variables (case-insensitive). */
export function detectForbiddenUnsubscribeTokens(
  variables: string[],
): Array<{ token: string; replacement: string }> {
  const seen = new Set<string>();
  const out: Array<{ token: string; replacement: string }> = [];
  for (const v of variables ?? []) {
    const key = (v ?? "").toLowerCase();
    if (key in FORBIDDEN_UNSUBSCRIBE_TOKENS && !seen.has(key)) {
      seen.add(key);
      out.push({ token: key, replacement: FORBIDDEN_UNSUBSCRIBE_TOKENS[key] });
    }
  }
  return out;
}

/** Human-readable block message naming each broken token and its exact replacement. */
export function forbiddenUnsubscribeMessage(
  found: Array<{ token: string; replacement: string }>,
): string {
  const parts = found
    .map((f) => `{{${f.token}}} → ${f.replacement}`)
    .join("; ");
  return (
    "This SendGrid template uses an unsubscribe tag that renders as an EMPTY link when the " +
    "CRM sends it. Edit the template in SendGrid and replace: " +
    parts +
    ". These links work only when the flight/sequence has an Email subscription category " +
    "selected."
  );
}

/**
 * SendGrid's own raw unsubscribe tags (`<%asm_group_unsubscribe_raw_url%>`,
 * `<%asm_preferences_raw_url%>`, ...) unsubscribe on a plain page load, so email security
 * scanners that pre-fetch links unsubscribe recipients automatically (2026-09-30 investigation).
 * Every marketing send path refuses a template containing one (templateGuard, always on); this
 * mirrors that check so the operator sees it before sending. Pattern matches the server's.
 */
const RAW_SENDGRID_UNSUB_TAG_RE = /<%\s*asm_[a-z_]+_url\s*%>/gi;

export function detectRawSendgridUnsubscribeTags(
  content: string | null | undefined,
): string[] {
  const seen = new Set<string>();
  for (const m of (content ?? "").matchAll(RAW_SENDGRID_UNSUB_TAG_RE)) {
    seen.add(m[0].replace(/\s+/g, "").toLowerCase());
  }
  return [...seen];
}

export function rawSendgridUnsubscribeMessage(
  tags: string[],
  templateName?: string | null,
): string {
  const name = templateName ? `"${templateName}"` : "This template";
  return (
    `${name} uses SendGrid's own unsubscribe link (${tags.join(", ")}), which email security ` +
    "scanners trigger automatically — people get unsubscribed without clicking. Sends with it " +
    "are refused. In SendGrid, replace it with {{{crm_unsubscribe_url}}} (and " +
    "{{{crm_preferences_url}}} for preferences), then pick the template again."
  );
}

/**
 * Check if a merge field is a SendGrid reserved field
 */
export function isSendGridReservedField(fieldName: string): boolean {
  return fieldName.toLowerCase() in SENDGRID_RESERVED_FIELDS;
}

/**
 * Get reserved field info (case-insensitive lookup)
 */
export function getReservedFieldInfo(
  fieldName: string,
): { label: string; description: string } | null {
  const key = fieldName.toLowerCase();
  return SENDGRID_RESERVED_FIELDS[key] || null;
}

// ========================================
// SENDGRID TEMPLATE OPERATIONS
// ========================================

/**
 * Get all SendGrid dynamic templates
 */
export async function listSendGridTemplates(): Promise<SendGridTemplate[]> {
  const { data: session } = await supabase.auth.getSession();

  if (!session?.session) {
    throw new Error("No active session");
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sendgrid-templates`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to fetch templates (${response.status})`,
    );
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || "Failed to fetch templates");
  }

  return data.templates as SendGridTemplate[];
}

/**
 * Get detailed information about a specific SendGrid template
 * including merge fields detected from the template content
 */
export async function getSendGridTemplateDetails(
  templateId: string,
): Promise<SendGridTemplateDetails> {
  const { data: session } = await supabase.auth.getSession();

  if (!session?.session) {
    throw new Error("No active session");
  }

  // Use direct fetch since functions.invoke doesn't support query params well
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sendgrid-template-details?template_id=${encodeURIComponent(templateId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to fetch template details (${response.status})`,
    );
  }

  const responseData = await response.json();

  if (!responseData.success) {
    throw new Error(responseData.error || "Failed to fetch template details");
  }

  return responseData.template as SendGridTemplateDetails;
}

/**
 * Search templates by name
 */
export async function searchSendGridTemplates(
  query: string,
): Promise<SendGridTemplate[]> {
  const templates = await listSendGridTemplates();

  if (!query.trim()) {
    return templates;
  }

  const lowerQuery = query.toLowerCase();
  return templates.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.active_version?.subject?.toLowerCase().includes(lowerQuery),
  );
}

// ========================================
// EMAIL TEMPLATE REGISTRY
// ========================================

export interface EmailTemplateRegistryRecord {
  id: string;
  sendgrid_template_id: string;
  name: string;
  description?: string;
  supports_voucher: boolean;
  supports_multi_offer: boolean;
  merge_fields: string[];
  category?: string;
  last_used_at?: string;
  usage_count: number;
  /** Soft-delete flag. Archived templates are hidden from the saved-list picker. */
  is_archived?: boolean;
  /**
   * TRUE only for templates a user explicitly starred from the Contact "Send Email" context.
   * The contact saved list filters on this; campaign rows leave it FALSE.
   */
  saved_for_contact_email?: boolean;
}

/**
 * Detect template capabilities from merge fields.
 * If fields include voucher_code or qr_image_url, supports_voucher = true.
 * If fields include 'offers' (array iteration), supports_multi_offer = true.
 */
export function detectTemplateCapabilities(mergeFields: string[]): {
  supports_voucher: boolean;
  supports_multi_offer: boolean;
} {
  const normalized = mergeFields.map((f) => f.toLowerCase());
  return {
    supports_voucher:
      normalized.includes("voucher_code") ||
      normalized.includes("qr_image_url"),
    supports_multi_offer: normalized.includes("offers"),
  };
}

/**
 * Upsert a template into the local registry.
 * Called automatically when a SendGrid template is selected in the flight form (campaign context),
 * and when a template is starred from the Contact "Send Email" modal (contact context).
 *
 * `savedForContactEmail` MUST be passed true ONLY from the contact-star paths. It flags the row for the
 * contact saved list AND un-archives it. The campaign flight picker omits it, so on conflict those two
 * columns are left untouched (PostgREST only updates keys present in the payload) — a campaign save can
 * therefore neither add a row to the contact list nor resurrect a contact-removed (archived) one.
 */
export async function upsertTemplateRegistry(
  template: SendGridTemplateDetails,
  opts: { savedForContactEmail?: boolean } = {},
): Promise<EmailTemplateRegistryRecord> {
  const capabilities = detectTemplateCapabilities(template.merge_fields);

  const record: Record<string, unknown> = {
    sendgrid_template_id: template.id,
    name: template.name,
    supports_voucher: capabilities.supports_voucher,
    supports_multi_offer: capabilities.supports_multi_offer,
    merge_fields: template.merge_fields,
    last_used_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  if (opts.savedForContactEmail) {
    // Contact "Send Email" star: add the row to the contact saved list and un-archive it (so a
    // previously-removed template can be re-added by starring it again).
    record.saved_for_contact_email = true;
    record.is_archived = false;
  }

  const { data, error } = await supabase
    .from("email_template_registry")
    .upsert(record, { onConflict: "sendgrid_template_id" })
    .select()
    .single();

  if (error) {
    console.error("Error upserting template registry:", error);
    throw new Error(`Failed to upsert template registry: ${error.message}`);
  }

  // Increment usage count
  await supabase
    .from("email_template_registry")
    .update({ usage_count: (data.usage_count || 0) + 1 })
    .eq("id", data.id);

  return data as EmailTemplateRegistryRecord;
}

/**
 * Get template registry entry by SendGrid template ID
 */
export async function getTemplateRegistry(
  sendgridTemplateId: string,
): Promise<EmailTemplateRegistryRecord | null> {
  const { data, error } = await supabase
    .from("email_template_registry")
    .select("*")
    .eq("sendgrid_template_id", sendgridTemplateId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching template registry:", error);
    return null;
  }

  return data as EmailTemplateRegistryRecord;
}

/**
 * List the Contact "Send Email" saved templates — ONLY those explicitly starred from the contact
 * context (saved_for_contact_email = true) and not removed (is_archived = false). This is distinct
 * from the full email_template_registry (which campaigns pre-populate); the contact list starts empty
 * and grows only as users star templates. Most-recently-used first. Bounded select (never an unbounded
 * scan) per the PostgREST bounded-select rule. Ordered by last_used_at (nulls last) then usage_count.
 */
export async function listTemplateRegistry(
  limit = 100,
): Promise<EmailTemplateRegistryRecord[]> {
  const { data, error } = await supabase
    .from("email_template_registry")
    .select("*")
    // Contact saved list = only templates explicitly starred from the Contact "Send Email" context…
    .eq("saved_for_contact_email", true)
    // …and not removed (soft-deleted).
    .eq("is_archived", false)
    .order("last_used_at", { ascending: false, nullsFirst: false })
    .order("usage_count", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error listing template registry:", error);
    throw new Error(`Failed to list saved templates: ${error.message}`);
  }

  return (data as EmailTemplateRegistryRecord[]) || [];
}

/**
 * Remove a template from the SHARED saved list (soft-delete). Calls the role-gated
 * archive_template_registry RPC (SECURITY DEFINER) which flips is_archived=true — restricted to
 * internal sender roles, since it affects every user's saved list. Re-selecting the template later
 * (upsertTemplateRegistry) un-archives it. Returns true when a visible row was archived, false when
 * nothing matched (already removed / unknown id).
 */
export async function removeTemplateFromRegistry(
  sendgridTemplateId: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("archive_template_registry", {
    p_sendgrid_template_id: sendgridTemplateId,
  });

  if (error) {
    console.error("Error removing template from registry:", error);
    throw new Error(`Failed to remove saved template: ${error.message}`);
  }

  return data === true;
}

// ========================================
// FIELD MAPPING UTILITIES
// ========================================

/**
 * Minimal Handlebars-style {{var}} / {{{var}}} substitution against a flat
 * variable map. Mirrors `substituteHandlebars` in
 * supabase/functions/_shared/flightSendHelpers.ts so the FlightForm subject
 * preview renders the same shape the production send produces. Unknown
 * variables become empty strings; we don't HTML-escape (subject text isn't
 * HTML).
 *
 * Keep in sync with the edge helper. The two are intentionally duplicated
 * because the edge runtime can't import frontend code and vice versa.
 */
export function substituteHandlebars(
  template: string,
  vars: Record<string, unknown>,
): string {
  return template.replace(/\{\{\{?\s*(\w+)\s*\}?\}\}/g, (_, name) => {
    const v = vars[name];
    if (v === undefined || v === null) {
      return "";
    }
    return String(v);
  });
}

/**
 * Extract the variable names referenced in a Handlebars-style template
 * string. e.g. "Hi {{first_name}}, code {{{voucher_code}}}"
 *   → ["first_name", "voucher_code"]
 */
export function extractHandlebarsVariables(template: string): string[] {
  const seen = new Set<string>();
  const re = /\{\{\{?\s*(\w+)\s*\}?\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(template)) !== null) {
    seen.add(match[1]);
  }
  return Array.from(seen);
}

/**
 * Detect whether a SendGrid-stored template subject is itself a placeholder
 * for the operator's per-flight override (the `{{{subject}}}` pattern). A
 * template configured this way REQUIRES the flight to supply a real subject;
 * the FlightForm should not auto-copy this back into flight.email_subject
 * (that would create a self-referential override resolving to empty).
 */
export function isSelfReferentialSubject(
  subject: string | null | undefined,
): boolean {
  if (!subject) {
    return false;
  }
  return extractHandlebarsVariables(subject).includes("subject");
}

/**
 * Merge fields a quick email / flight always populates, so a subject conditioned
 * on one of these (`{{#if first_name}}…{{/if}}`) always renders its IF branch.
 */
const SUPPORTED_SUBJECT_MERGE_FIELDS = ["first_name", "last_name", "email"];

/**
 * Derive a usable, pre-fillable subject from a SendGrid template subject that
 * uses a Handlebars `{{#if …}}…{{else}}…{{/if}}` conditional. Handles:
 *
 *  - The standard "overridable subject" idiom
 *    `{{#if subject}}{{subject}}{{else}}Default subject{{/if}}` — the caller MAY
 *    override by passing `subject`, but the template has a default in `{{else}}`.
 *    isSelfReferentialSubject() flags this whole idiom (it contains `{{subject}}`),
 *    which would wrongly force the operator to type a subject even though a literal
 *    fallback exists ("It doesn't prepopulate from the ELSE"). → returns the
 *    `{{else}}` default.
 *  - A conditional keyed on a merge field we DO populate
 *    (`{{#if first_name}}Hi {{first_name}}{{else}}Hi{{/if}}`). Per operator request:
 *    if first_name / last_name / email appears in the subject, prefer the branch
 *    that USES it over the static fallback. Since that field is always present, the
 *    IF branch renders → returns the IF branch (personalized).
 *
 * Any simple `{{merge}}` tokens in the returned text are left intact — they resolve
 * at send time from dynamic_template_data. Returns null when there is no conditional
 * (caller uses the raw subject) or the chosen branch is empty.
 */
export function extractOverridableSubjectFallback(
  subject: string | null | undefined,
): string | null {
  if (!subject) {
    return null;
  }
  const m = subject.match(
    /\{\{\s*#if\s+([\w.]+)\s*\}\}([\s\S]*?)\{\{\s*else\s*\}\}([\s\S]*?)\{\{\s*\/if\s*\}\}/i,
  );
  if (!m) {
    return null;
  }
  const condition = m[1].toLowerCase();
  const ifBranch = m[2].trim();
  const elseBranch = m[3].trim();
  // Condition on a merge field we populate → the IF branch always renders; prefer
  // its personalized text over the static fallback.
  if (SUPPORTED_SUBJECT_MERGE_FIELDS.includes(condition)) {
    if (ifBranch.length > 0) {
      return ifBranch;
    }
    return elseBranch.length > 0 ? elseBranch : null;
  }
  // Overridable `{{#if subject}}` idiom (or any other condition) → the `{{else}}` default.
  return elseBranch.length > 0 ? elseBranch : null;
}

/**
 * Validate a per-flight subject override.
 *
 * `knownVariables` should be the set of variable names that will be present
 * in dynamic_template_data at send time — the template's merge_fields plus
 * system fallbacks (email/first_name/last_name) plus any Phase 2 auto-
 * injected variables.
 *
 * Returns:
 *   - hardError: a string when the override is fundamentally broken (self-
 *     reference). Block save.
 *   - unknownVars: variables in the subject not in `knownVariables`. Soft
 *     warning — render as a hint, don't block.
 */
export function validateSubjectOverride(
  subject: string,
  knownVariables: ReadonlySet<string>,
): { hardError: string | null; unknownVars: string[] } {
  if (isSelfReferentialSubject(subject)) {
    return {
      hardError:
        "Subject can't reference {{subject}} — that would resolve to itself and produce an empty subject.",
      unknownVars: [],
    };
  }
  const referenced = extractHandlebarsVariables(subject);
  const unknown = referenced.filter((v) => !knownVariables.has(v));
  return { hardError: null, unknownVars: unknown };
}

/**
 * Build template_data object from field mappings and contact/voucher data
 */
export function buildTemplateData(
  mappings: FieldMapping[],
  contact: Record<string, unknown>,
  voucher?: Record<string, unknown>,
  contentItems?: Record<string, unknown>,
): Record<string, unknown> {
  const templateData: Record<string, unknown> = {};

  for (const mapping of mappings) {
    // Skip SendGrid reserved fields — they're auto-injected at send time
    if (mapping.sourceType === "sendgrid") {
      continue;
    }

    let value: unknown;

    switch (mapping.sourceType) {
      case "contact":
        value = mapping.sourceField ? contact[mapping.sourceField] : undefined;
        break;
      case "voucher":
        value =
          mapping.sourceField && voucher
            ? voucher[mapping.sourceField]
            : undefined;
        break;
      case "content":
        value =
          mapping.sourceField && contentItems
            ? contentItems[mapping.sourceField]
            : undefined;
        break;
      case "url":
        value = mapping.customValue;
        break;
      case "custom":
        value = mapping.customValue;
        break;
      case "landing_page":
        // The actual URL is built server-side at send time using
        // metadata.landing_page_config + the recipient's voucher. For client-
        // side preview only, render a clearly-labeled placeholder so the
        // operator sees that the variable will be populated.
        value = "[landing page link — built at send time]";
        break;
    }

    // Handle system fields
    if (mapping.sourceType === "custom") {
      if (mapping.customValue === "{{current_date}}") {
        value = new Date().toLocaleDateString();
      } else if (mapping.customValue === "{{current_year}}") {
        value = new Date().getFullYear().toString();
      }
    }

    if (value !== undefined && value !== null && value !== "") {
      templateData[mapping.templateField] = value;
    }
  }

  return templateData;
}

/**
 * Suggest mappings based on field name similarity
 */
export function suggestFieldMapping(
  templateField: string,
): FieldMapping | null {
  // Check SendGrid reserved fields first
  if (isSendGridReservedField(templateField)) {
    return {
      templateField,
      sourceType: "sendgrid",
    };
  }

  const normalizedField = templateField.toLowerCase().replace(/[_-]/g, "");

  // Tracked landing-page link — surface the URL builder for this var.
  if (
    normalizedField === "landingpagelink" ||
    normalizedField === "landingurl"
  ) {
    return {
      templateField,
      sourceType: "landing_page",
    };
  }

  // Check contact fields
  for (const field of CONTACT_FIELDS) {
    const normalizedContactField = field.value
      .toLowerCase()
      .replace(/[_-]/g, "");
    if (
      normalizedField === normalizedContactField ||
      normalizedField.includes(normalizedContactField) ||
      normalizedContactField.includes(normalizedField)
    ) {
      return {
        templateField,
        sourceType: "contact",
        sourceField: field.value,
      };
    }
  }

  // Check voucher fields
  for (const field of VOUCHER_FIELDS) {
    const normalizedVoucherField = field.value
      .toLowerCase()
      .replace(/[_-]/g, "");
    if (
      normalizedField === normalizedVoucherField ||
      normalizedField.includes(normalizedVoucherField) ||
      normalizedVoucherField.includes(normalizedField)
    ) {
      return {
        templateField,
        sourceType: "voucher",
        sourceField: field.value,
      };
    }
  }

  // Common field name variations
  const commonMappings: Record<
    string,
    { sourceType: "contact" | "voucher"; sourceField: string }
  > = {
    name: { sourceType: "contact", sourceField: "first_name" },
    firstname: { sourceType: "contact", sourceField: "first_name" },
    lastname: { sourceType: "contact", sourceField: "last_name" },
    fname: { sourceType: "contact", sourceField: "first_name" },
    lname: { sourceType: "contact", sourceField: "last_name" },
    phone: { sourceType: "contact", sourceField: "phone_number" },
    company: { sourceType: "contact", sourceField: "full_name" },
    title: { sourceType: "contact", sourceField: "title" },
    jobtitle: { sourceType: "contact", sourceField: "title" },
    zip: { sourceType: "contact", sourceField: "postal_code" },
    zipcode: { sourceType: "contact", sourceField: "postal_code" },
    postalcode: { sourceType: "contact", sourceField: "postal_code" },
    memberstatus: { sourceType: "contact", sourceField: "member_status" },
    membershipstatus: { sourceType: "contact", sourceField: "member_status" },
    code: { sourceType: "voucher", sourceField: "voucher_code" },
    discount: { sourceType: "voucher", sourceField: "voucher_amount" },
    amount: { sourceType: "voucher", sourceField: "voucher_amount" },
    qrcode: { sourceType: "voucher", sourceField: "qr_image_url" },
    qr: { sourceType: "voucher", sourceField: "qr_image_url" },
    expiration: { sourceType: "voucher", sourceField: "expiration_date" },
    expires: { sourceType: "voucher", sourceField: "expiration_date" },
  };

  if (commonMappings[normalizedField]) {
    return {
      templateField,
      ...commonMappings[normalizedField],
    };
  }

  return null;
}

/**
 * Auto-suggest mappings for all template merge fields
 */
export function autoSuggestMappings(mergeFields: string[]): FieldMapping[] {
  return mergeFields.map((field) => {
    const suggestion = suggestFieldMapping(field);
    if (suggestion) {
      return suggestion;
    }
    // Default to custom with empty value for unrecognized fields
    return {
      templateField: field,
      sourceType: "custom",
      customValue: "",
    };
  });
}

/**
 * Normalize a lookup-related string for case-insensitive trim compare.
 * Used everywhere a contact attribute is matched against a lookup key
 * or a filter rule's value. Mirrors the SQL definition of
 * key_value_normalized: LOWER(TRIM(value)).
 */
function normalizeForLookup(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim().toLowerCase();
}

/**
 * Evaluate a lookup mapping's filter against one row's data. Returns
 * true if the row passes (or if the filter is empty/absent). All
 * comparisons are case-insensitive trim compares. Rules AND together —
 * Round-3 v1 does not support OR groups.
 *
 * Operator semantics (must match the edge-function mirror in
 * supabase/functions/_shared/flightSendHelpers.ts):
 *   eq / ne     — equality on the normalized cell value
 *   in / not_in — membership in normalized rule.value (array)
 *   is_truthy   — cell ∈ {yes, true, 1, y}
 *   is_falsy    — cell ∈ {no, false, 0, n, ""}
 */
export function evaluateLookupFilter(
  data: Record<string, unknown>,
  filter: LookupFilterRule[] | undefined,
): boolean {
  if (!filter || filter.length === 0) {
    return true;
  }
  for (const rule of filter) {
    const cell = normalizeForLookup(data[rule.column]);
    switch (rule.operator) {
      case "eq":
        if (cell !== normalizeForLookup(rule.value as string | undefined)) {
          return false;
        }
        break;
      case "ne":
        if (cell === normalizeForLookup(rule.value as string | undefined)) {
          return false;
        }
        break;
      case "in": {
        const list = Array.isArray(rule.value) ? rule.value : [];
        if (!list.map(normalizeForLookup).includes(cell)) {
          return false;
        }
        break;
      }
      case "not_in": {
        const list = Array.isArray(rule.value) ? rule.value : [];
        if (list.map(normalizeForLookup).includes(cell)) {
          return false;
        }
        break;
      }
      case "is_truthy":
        if (!["yes", "true", "1", "y"].includes(cell)) {
          return false;
        }
        break;
      case "is_falsy":
        if (!["no", "false", "0", "n", ""].includes(cell)) {
          return false;
        }
        break;
      default:
        // Unknown operator → treat as filter failure to be safe.
        return false;
    }
  }
  return true;
}

/**
 * Resolve a single lookup-typed mapping for one contact. Returns the
 * outcome bucket and the string value to substitute.
 *
 * Resolution chain (per Round-3 spec):
 *   1. Get contact[lookupKeyContactField], normalize.
 *      If empty → bucket=empty_key, value=fallback or "".
 *   2. Match the normalized key in lookupIndex.byKey.
 *      If a row matches:
 *        - Apply filter. If passes → bucket=matched, return value column.
 *        - If filter fails:
 *            try default row. If default passes filter → bucket=default_used.
 *            else → bucket=filter_excluded, value=fallback or "".
 *   3. No match: try default row.
 *        - Default exists and passes filter → bucket=default_used.
 *        - Otherwise → bucket=fallback_used, value=fallback or "".
 *
 * `resolvedValue` is always a string (possibly empty).
 *
 * NOTE: this implementation must stay in lockstep with its edge-function
 * mirror in supabase/functions/_shared/flightSendHelpers.ts. When you
 * change semantics here, change them there too.
 */
export function resolveLookupMapping(
  mapping: FieldMapping,
  contact: Record<string, unknown>,
  bucket: LookupBucket | undefined,
): LookupResolveOutcome {
  const fallback = mapping.lookupFallback ?? "";

  // Static content-bundle mode: every recipient receives the selected value from the one row
  // explicitly marked as default. Evaluated BEFORE the generic bucket/value-column fallback guards
  // so STRUCTURAL ABSENCE — a deleted lookup (no bucket), missing value column, deleted/filter-excluded
  // default row, or a mapped column absent from the starred row — fails closed (default_row_missing),
  // never fallback_used; otherwise a deleted single-row newsletter would send blank shared content.
  // Never guess the first row. A present-but-empty cell is an intentional blank section (default_used).
  if (mapping.lookupResolution === "default_row") {
    if (
      !bucket ||
      !mapping.lookupValueColumn ||
      !bucket.defaultRow ||
      !evaluateLookupFilter(bucket.defaultRow, mapping.lookupFilter) ||
      !Object.prototype.hasOwnProperty.call(
        bucket.defaultRow,
        mapping.lookupValueColumn,
      )
    ) {
      return { bucket: "default_row_missing", resolvedValue: fallback };
    }
    return {
      bucket: "default_used",
      resolvedValue: stringValue(bucket.defaultRow[mapping.lookupValueColumn]),
    };
  }

  // No bucket means the lookup wasn't loaded (deleted? not referenced?).
  // Treat as fallback-only (keyed mappings only — default_row handled above).
  if (!bucket) {
    return { bucket: "fallback_used", resolvedValue: fallback };
  }

  if (!mapping.lookupValueColumn) {
    return { bucket: "fallback_used", resolvedValue: fallback };
  }

  if (!mapping.lookupKeyContactField) {
    return { bucket: "fallback_used", resolvedValue: fallback };
  }

  const key = normalizeForLookup(contact[mapping.lookupKeyContactField]);
  if (key === "") {
    // No contact value → use default if it passes the filter,
    // otherwise fall back.
    if (
      bucket.defaultRow &&
      evaluateLookupFilter(bucket.defaultRow, mapping.lookupFilter)
    ) {
      return {
        bucket: "default_used",
        resolvedValue: stringValue(
          bucket.defaultRow[mapping.lookupValueColumn],
        ),
      };
    }
    return { bucket: "empty_key", resolvedValue: fallback };
  }

  const matchedRow = bucket.byKey.get(key);

  if (matchedRow) {
    if (evaluateLookupFilter(matchedRow, mapping.lookupFilter)) {
      return {
        bucket: "matched",
        resolvedValue: stringValue(matchedRow[mapping.lookupValueColumn]),
      };
    }
    // Matched a row, but filter rejected. Try default if distinct.
    if (
      bucket.defaultRow &&
      bucket.defaultRow !== matchedRow &&
      evaluateLookupFilter(bucket.defaultRow, mapping.lookupFilter)
    ) {
      return {
        bucket: "default_used",
        resolvedValue: stringValue(
          bucket.defaultRow[mapping.lookupValueColumn],
        ),
      };
    }
    return { bucket: "filter_excluded", resolvedValue: fallback };
  }

  // No match. Try default.
  if (
    bucket.defaultRow &&
    evaluateLookupFilter(bucket.defaultRow, mapping.lookupFilter)
  ) {
    return {
      bucket: "default_used",
      resolvedValue: stringValue(bucket.defaultRow[mapping.lookupValueColumn]),
    };
  }
  return { bucket: "fallback_used", resolvedValue: fallback };
}

function stringValue(v: unknown): string {
  if (v === null || v === undefined) {
    return "";
  }
  return String(v);
}

/**
 * Preview-path structural check: return the template fields of every static
 * content bundle (`default_row`) mapping whose starred default row is missing —
 * deleted lookup/row, missing value column, or the mapped column absent from the
 * starred row (resolver bucket `default_row_missing`). Preview must surface these
 * as a prominent error rather than render a false blank; send-time and pre-send
 * coverage already fail closed. Call ONLY after the preview lookup index has
 * finished loading — an empty index mid-load looks identical to a deleted lookup.
 * A present-but-empty cell is an intentional blank section and is NOT reported.
 */
export function findMissingDefaultRowBundles(
  mappings: FieldMapping[],
  lookupIndex: Map<string, LookupBucket> | undefined,
): string[] {
  const broken: string[] = [];
  for (const m of mappings) {
    if (m.sourceType !== "lookup" || m.lookupResolution !== "default_row") {
      continue;
    }
    const bucket = m.lookupId ? lookupIndex?.get(m.lookupId) : undefined;
    if (resolveLookupMapping(m, {}, bucket).bucket === "default_row_missing") {
      broken.push(m.templateField);
    }
  }
  return broken;
}

/**
 * Resolve a single FieldMapping to a concrete string value using a contact record.
 * Used for preview substitution. Voucher fields return a placeholder at preview time.
 *
 * For sourceType='lookup' you must also pass `lookupIndex` keyed by
 * lookupId. If the index is missing or the lookup isn't in it, the
 * mapping resolves to its fallback (or empty string).
 */
export function resolveFieldValue(
  mapping: FieldMapping,
  contact: Record<string, unknown>,
  lookupIndex?: Map<string, LookupBucket>,
  contentUrls?: Record<string, string>,
): string {
  switch (mapping.sourceType) {
    case "contact": {
      if (!mapping.sourceField) {
        return "";
      }
      const raw = contact[mapping.sourceField];
      if (raw === null || raw === undefined) {
        return "";
      }
      if (
        mapping.sourceField === "member_start_date" ||
        mapping.sourceField === "member_end_date"
      ) {
        try {
          return new Date(String(raw)).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        } catch {
          return String(raw);
        }
      }
      return String(raw);
    }
    case "custom":
    case "url":
      return mapping.customValue || "";
    case "voucher":
      return `[voucher: ${mapping.sourceField || "field"}]`;
    case "sendgrid":
      return "";
    case "lookup": {
      const bucket = mapping.lookupId
        ? lookupIndex?.get(mapping.lookupId)
        : undefined;
      return resolveLookupMapping(mapping, contact, bucket).resolvedValue;
    }
    case "content": {
      // v1: IMAGE Content Items resolved by the caller (preview path
      // calls content.service.ts:resolveContentUrlsForMappings; send
      // path uses the backend equivalent). If the URL isn't in the
      // map — either because the caller didn't pass one, or because
      // validation flagged the reference — render a visible
      // placeholder so the operator notices BEFORE sending.
      if (!mapping.sourceField || !contentUrls) {
        return "[content missing — pick an IMAGE Content Item]";
      }
      const url = contentUrls[mapping.sourceField];
      if (!url) {
        return "[content missing — pick an IMAGE Content Item]";
      }
      return url;
    }
    default:
      return "";
  }
}

/**
 * Build a full substitution map from field mappings + a contact record.
 * Replaces {{variable}} patterns in template HTML for preview.
 *
 * Pass `lookupIndex` to support sourceType='lookup' mappings; without
 * it, those mappings resolve to their configured fallback (or "").
 */
export function buildPreviewSubstitutions(
  mappings: FieldMapping[],
  contact: Record<string, unknown>,
  lookupIndex?: Map<string, LookupBucket>,
  contentUrls?: Record<string, string>,
): Record<string, string> {
  const subs: Record<string, string> = {};
  for (const mapping of mappings) {
    subs[mapping.templateField] = resolveFieldValue(
      mapping,
      contact,
      lookupIndex,
      contentUrls,
    );
  }
  // Always inject system fields
  subs["current_date"] = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  subs["current_year"] = String(new Date().getFullYear());
  return subs;
}

/**
 * Substitute {{variable}} patterns in a template HTML string.
 * Unmatched variables are left as-is so the user can see what's unmapped.
 */
export function substituteTemplateVariables(
  html: string,
  subs: Record<string, string>,
): string {
  // Resolve {{{field}}} (raw-HTML slot in SendGrid) and {{field}} alike. Triple is
  // listed first in the alternation so it is consumed whole and the double-brace
  // branch can't match inside it. The old single {{ }} regex captured "{field"
  // out of a triple and never resolved it — that was the "triple braces don't work
  // in preview" bug. Preview shows the mapped value for both forms; double-vs-triple
  // escaping is SendGrid's job at send time. Unmapped fields are left intact.
  return html.replace(
    /\{\{\{\s*([^{}]+?)\s*\}\}\}|\{\{\s*([^{}]+?)\s*\}\}/g,
    (
      match,
      tripleField: string | undefined,
      doubleField: string | undefined,
    ) => {
      const field = (tripleField ?? doubleField ?? "").trim();
      return field in subs ? subs[field] : match;
    },
  );
}

/**
 * Validate field mappings - ensure all required fields are mapped
 */
export function validateFieldMappings(
  mappings: FieldMapping[],
  requiredFields: string[],
): { valid: boolean; missingFields: string[] } {
  const mappedFields = new Set(mappings.map((m) => m.templateField));
  const missingFields = requiredFields.filter((f) => !mappedFields.has(f));

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
